import React, { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  fetchAdminPosts,
  createPost,
  updatePost,
  deletePost,
  togglePostVisibility,
  fetchBookings,
  fetchAdminEvents,
  createEvent,
  updateEvent,
  deleteEvent,
  fetchMembers,
  updateMember,
} from "../services/adminApi";

// ─── Admin sub-components ───
import CreatePostForm from "../components/Admin/CreatePostForm";
import PostList from "../components/Admin/PostList";
import EventManager from "../components/Admin/EventManager";
import SeminarManager from "../components/Admin/SeminarManager";
import MembersList from "../components/Admin/MembersList";

function Admin() {
  const [activeTab, setActiveTab] = useState("create");
  const [posts, setPosts] = useState([]);
  const [bookings, setBookings] = useState({});
  const [events, setEvents] = useState([]);
  const [members, setMembers] = useState([]);

  useEffect(() => {
    fetchAdminPosts().then(setPosts).catch(console.error);
    fetchAdminEvents().then(setEvents).catch(console.error);
    fetchMembers().then(setMembers).catch(console.error);
  }, []);

  // Create a new appointment post
  const handleCreatePost = async (postData) => {
    try {
      const newPost = await createPost(postData);
      setPosts((prev) => [...prev, newPost]);
      setActiveTab("manage");
      toast.success("✅ Konsultasjon opprettet!");
    } catch (err) {
      console.error(err);
      toast.error("❌ Kunne ikke opprette konsultasjon.");
    }
  };

  // Update a post
  const handleUpdatePost = async (id, updates) => {
    try {
      const updated = await updatePost(id, updates);
      setPosts((prev) => prev.map((p) => (p.id === id ? updated : p)));
      toast.success("✅ Konsultasjon oppdatert!");
    } catch (err) {
      console.error(err);
      toast.error("❌ Kunne ikke oppdatere konsultasjon.");
    }
  };

  // Delete a post
  const handleDeletePost = async (id) => {
    try {
      await deletePost(id);
      setPosts((prev) => prev.filter((p) => p.id !== id));
      setBookings((prev) => {
        const copy = { ...prev };
        delete copy[id];
        return copy;
      });
      toast.info("🗑️ Konsultasjon slettet");
    } catch (err) {
      console.error(err);
      toast.error("❌ Kunne ikke slette konsultasjon.");
    }
  };

  // Toggle post visibility (show / hide)
  const handleToggleVisibility = async (id, visible) => {
    try {
      const updated = await togglePostVisibility(id, visible);
      setPosts((prev) => prev.map((p) => (p.id === id ? updated : p)));
      toast.success(visible ? "👁️ Post er nå synlig" : "🙈 Post er nå skjult");
    } catch (err) {
      console.error(err);
      toast.error("❌ Kunne ikke oppdatere synlighet.");
    }
  };

  // Load bookings for a specific post
  const handleLoadBookings = async (id) => {
    try {
      const data = await fetchBookings(id);
      setBookings((prev) => ({ ...prev, [id]: data }));
    } catch (err) {
      console.error(err);
      toast.error("❌ Kunne ikke laste påmeldinger.");
    }
  };

  // ─── Event handlers ───
  const handleCreateEvent = async (eventData) => {
    try {
      const newEvent = await createEvent(eventData);
      setEvents((prev) => [...prev, newEvent]);
      toast.success("✅ Arrangement opprettet!");
    } catch (err) {
      console.error(err);
      toast.error("❌ Kunne ikke opprette arrangement.");
    }
  };

  const handleUpdateEvent = async (id, updates) => {
    try {
      const updated = await updateEvent(id, updates);
      setEvents((prev) => prev.map((e) => (e.id === id ? updated : e)));
      toast.success("✅ Arrangement oppdatert!");
    } catch (err) {
      console.error(err);
      toast.error("❌ Kunne ikke oppdatere arrangement.");
    }
  };

  const handleDeleteEvent = async (id) => {
    try {
      await deleteEvent(id);
      setEvents((prev) => prev.filter((e) => e.id !== id));
      toast.info("🗑️ Arrangement slettet");
    } catch (err) {
      console.error(err);
      toast.error("❌ Kunne ikke slette arrangement.");
    }
  };

  return (
    <div className="container py-4">
      <ToastContainer position="top-center" autoClose={3000} />
      <h1 className="mb-4">⚙️ Administrasjonspanel</h1>

      {/* ─── Tab Navigation ─── */}
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "create" ? "active" : ""}`}
            onClick={() => setActiveTab("create")}
          >
            ➕ Ny Konsultasjon
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "manage" ? "active" : ""}`}
            onClick={() => setActiveTab("manage")}
          >
            📋 Administrer Poster
            {posts.length > 0 && (
              <span className="badge bg-primary ms-2">{posts.length}</span>
            )}
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "events" ? "active" : ""}`}
            onClick={() => setActiveTab("events")}
          >
            🎉 Arrangementer
            {events.length > 0 && (
              <span className="badge bg-primary ms-2">{events.length}</span>
            )}
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "seminarer" ? "active" : ""}`}
            onClick={() => setActiveTab("seminarer")}
          >
            🎓 Seminarer
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "members" ? "active" : ""}`}
            onClick={() => setActiveTab("members")}
          >
            👥 Medlemmer
            {members.length > 0 && (
              <span className="badge bg-primary ms-2">{members.length}</span>
            )}
          </button>
        </li>
      </ul>

      {/* ─── Tab Content ─── */}
      {activeTab === "create" && (
        <CreatePostForm onCreatePost={handleCreatePost} />
      )}

      {activeTab === "manage" && (
        <PostList
          posts={posts}
          bookings={bookings}
          onToggleVisibility={handleToggleVisibility}
          onLoadBookings={handleLoadBookings}
          onUpdatePost={handleUpdatePost}
          onDeletePost={handleDeletePost}
        />
      )}

      {activeTab === "events" && (
        <EventManager
          events={events}
          onCreateEvent={handleCreateEvent}
          onUpdateEvent={handleUpdateEvent}
          onDeleteEvent={handleDeleteEvent}
        />
      )}

      {activeTab === "seminarer" && (
        <SeminarManager />
      )}

      {activeTab === "members" && (
        <MembersList
          members={members}
          onUpdateMember={async (id, updates) => {
            try {
              const updated = await updateMember(id, updates);
              setMembers((prev) => prev.map((m) => (m.numericId === id ? { ...m, ...updated } : m)));
              toast.success("✅ Medlem oppdatert!");
            } catch (err) {
              toast.error("❌ Kunne ikke oppdatere medlem.");
            }
          }}
        />
      )}
    </div>
  );
}

export default Admin;
